<template>
    <div class="space-y-6">
        <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <Select
                v-model.number="localValue.rider_id"
                name="Rider"
                :label="$t('user.rider')"
                :disabled="disabled"
                :empty-option-label="$t('form.none')"
                :empty-option-value="null"
            >
                <option
                    v-for="rider in riderOptions"
                    :key="getRiderOptionValue(rider)"
                    :value="getRiderOptionValue(rider)"
                >
                    {{ getRiderOptionLabel(rider) }}
                </option>
            </Select>

            <Select
                v-model.number="localValue.horse_id"
                name="Horse"
                :label="$t('horse.title')"
                :disabled="disabled"
                :empty-option-label="$t('form.none')"
                :empty-option-value="null"
            >
                <option
                    v-for="horse in horseOptions"
                    :key="getHorseOptionValue(horse)"
                    :value="getHorseOptionValue(horse)"
                >
                    {{ getHorseOptionLabel(horse) }}
                </option>
            </Select>
        </div>

        <Input
            v-model="localValue.name"
            :placeholder="$t('session.name-example')"
            type="text"
            :label="$t('horse.name')"
            :disabled="disabled"
        />

        <Input
            v-model="localValue.description"
            :placeholder="$t('session.description-example')"
            type="text"
            :label="$t('session.description')"
            :disabled="disabled"
        />

        <Input v-model="localValue.start_at" type="datetime-local" :label="$t('event.date')" :disabled="disabled" />

        <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <Input
                v-model="localValue.city"
                placeholder="Lausanne"
                type="text"
                :label="$t('location.city')"
                :disabled="disabled"
            />

            <Radio
                v-model.number="localValue.indoor"
                :options="[
                    {
                        id: 0,
                        value: $t('session.location.outdoor'),
                    },
                    {
                        id: 1,
                        value: $t('session.location.indoor'),
                    },
                    {
                        id: 2,
                        value: $t('session.location.mixed'),
                    },
                ]"
                :label="$t('session.location.title')"
                :disabled="disabled"
            />
        </div>

        <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <Select
                v-if="showDiscipline"
                v-model.number="localValue.discipline"
                name="Discipline"
                :label="$t('session.discipline.title')"
                :disabled="disabled"
                :empty-option-label="$t('form.none')"
                :empty-option-value="null"
            >
                <option v-for="(item, index) in metas['session-discipline']" :key="index" :value="index">
                    {{ $t(`session.discipline.${item?.toLowerCase()?.replaceAll(' ', '-')}`) }}
                </option>
            </Select>

            <Select
                v-model.number="localValue.type"
                name="Type"
                :label="$t('session.type.title')"
                :disabled="disabled"
                :empty-option-label="$t('form.none')"
                :empty-option-value="null"
            >
                <option v-for="(item, index) in metas['session-type']" :key="index" :value="index">
                    {{ $t(`session.type.${item?.toLowerCase()?.replaceAll(' ', '-')}`) }}
                </option>
            </Select>
        </div>

        <template v-if="showMetrics">
            <DurationInput
                :label="$t('metric.duration')"
                v-model="localValue.elapsed_time"
                :wrapperClass="'bg-dark-light/80'"
            />

            <GaitDurationAllocator :totalSeconds="localValue.elapsed_time" v-model="localValueGaits" />

            <Input
                v-model.number="localValue.distance"
                :label="$t('metric.distance')"
                type="number"
                unit="m"
                :userUnit="userUnits.distance"
                min="0"
                :disabled="disabled"
            />

            <GaitDistanceAllocator
                :totalMeters="localValue.distance || 0"
                v-model="localValueDistances"
                :userUnit="userUnits.distance"
                :userSpeedUnit="userUnits.speed"
                :gaitTimes="localValueGaits"
            />
        </template>
    </div>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { parseNullableId } from '@/lib/helpers/numeric'
import { withKeptHorse } from '@/lib/horses/archived'
import { GAIT_TIME_KEYS, gaitsExceedDuration } from '@/lib/sessions/gait-total'
import Input from '@/components/form/Input.vue'
import Select from '@/components/form/Select.vue'
import Radio from '@/components/form/Radio.vue'
import DurationInput from '@/components/form/DurationInput.vue'
import GaitDurationAllocator from '@/components/sessions/GaitDurationAllocator.vue'
import GaitDistanceAllocator from '@/components/sessions/GaitDistanceAllocator.vue'

const props = defineProps({
    modelValue: { type: Object, default: () => ({}) },
    metas: { type: Object, default: () => ({}) },
    horses: { type: Array, default: () => [] },
    riders: { type: Array, default: () => [] },
    userUnits: { type: Object, default: () => ({ distance: 'm' }) },
    disabled: { type: Boolean, default: false },
    showMetrics: { type: Boolean, default: true },
    // Off when the parent offers its own discipline control (ProgramManualSessionModal).
    showDiscipline: { type: Boolean, default: true },
})
const emits = defineEmits(['update:modelValue'])
useI18n()

const firstMetaIndex = (key) => {
    const keys = props.metas?.[key] ? Object.keys(props.metas[key]) : []
    return keys.length ? parseInt(keys[0]) : null
}

const defaultValue = () => ({
    horse_id: null,
    rider_id: null,
    start_at: '',
    name: '',
    description: '',
    discipline: firstMetaIndex('session-discipline'),
    type: firstMetaIndex('session-type'),
    indoor: 0,
    city: '',
    distance: null,
    elapsed_time: 0,
    walk_time: 0,
    trot_time: 0,
    canter_time: 0,
    standing_time: 0,
    walk_distance: 0,
    trot_distance: 0,
    canter_distance: 0,
    standing_distance: 0,
})

const localValue = reactive(defaultValue())
const localValueGaits = computed({
    get: () => Object.fromEntries(GAIT_TIME_KEYS.map((key) => [key, localValue[key]])),
    set: (val) => {
        GAIT_TIME_KEYS.forEach((key) => {
            localValue[key] = Math.max(0, val?.[key] ?? 0)
        })
    },
})

const localValueDistances = computed({
    get: () => ({
        walk_distance: localValue.walk_distance,
        trot_distance: localValue.trot_distance,
        canter_distance: localValue.canter_distance,
        standing_distance: localValue.standing_distance,
    }),
    set: (val) => {
        localValue.walk_distance = Math.max(0, val?.walk_distance ?? 0)
        localValue.trot_distance = Math.max(0, val?.trot_distance ?? 0)
        localValue.canter_distance = Math.max(0, val?.canter_distance ?? 0)
        localValue.standing_distance = Math.max(0, val?.standing_distance ?? 0)
    },
})

const getHorseOptionValue = (horse) => parseNullableId(horse?.id ?? horse?.horse_id)

const getHorseOptionLabel = (horse) => horse?.name || (getHorseOptionValue(horse) ? `#${getHorseOptionValue(horse)}` : '')

const getRiderOptionValue = (rider) => parseNullableId(rider?.id ?? rider?.rider_id)

const getRiderOptionLabel = (rider) =>
    [rider?.first_name, rider?.last_name].filter(Boolean).join(' ') ||
    rider?.name ||
    (getRiderOptionValue(rider) ? `#${getRiderOptionValue(rider)}` : '')

// The session's own horse stays listed even once archived, so editing the session still shows it.
const horseOptions = computed(() =>
    withKeptHorse(Array.isArray(props.horses) ? props.horses : [], props.modelValue?.horse)
)

const riderOptions = computed(() => {
    const options = Array.isArray(props.riders) ? [...props.riders] : []
    const selectedRiderId = parseNullableId(localValue.rider_id)
    const sessionRider = props.modelValue?.rider
    const sessionRiderId = getRiderOptionValue(sessionRider)

    if (
        sessionRider &&
        sessionRiderId != null &&
        selectedRiderId === sessionRiderId &&
        !options.some((rider) => getRiderOptionValue(rider) === sessionRiderId)
    ) {
        options.push(sessionRider)
    }

    return options
})

watch(
    () => props.modelValue,
    (val) => {
        Object.assign(localValue, defaultValue(), val || {})
        if (props.horses?.length && localValue.horse_id === undefined) {
            localValue.horse_id = props.horses[0]?.id
        }
        if (props.riders?.length && localValue.rider_id === undefined) {
            localValue.rider_id = props.riders[0]?.id
        }
    },
    { immediate: true, deep: true }
)

watch(
    () => props.horses,
    () => {
        if (props.horses?.length && localValue.horse_id === undefined) {
            localValue.horse_id = props.horses[0]?.id
        }
    },
    { immediate: true, deep: true }
)

watch(
    () => props.riders,
    () => {
        if (props.riders?.length && localValue.rider_id === undefined) {
            localValue.rider_id = props.riders[0]?.id
        }
    },
    { immediate: true, deep: true }
)

watch(
    () => props.metas,
    () => {
        if (localValue.discipline === undefined) {
            localValue.discipline = firstMetaIndex('session-discipline')
        }
        if (localValue.type === undefined) {
            localValue.type = firstMetaIndex('session-type')
        }
    },
    { immediate: true, deep: true }
)

watch(
    localValue,
    (val) => {
        emits('update:modelValue', { ...val })
    },
    { deep: true }
)

const resetGaits = () => {
    GAIT_TIME_KEYS.forEach((key) => {
        localValue[key] = 0
    })
}

// Whole seconds on both sides, like the allocator: a float duration (phone
// session, 1799.64 s) must not wipe gaits that fill its rounded total (1800 s).
const enforceElapsedConsistency = () => {
    if (gaitsExceedDuration(localValue, localValue.elapsed_time)) {
        resetGaits()
    }
}

watch(
    () => localValue.elapsed_time,
    () => {
        enforceElapsedConsistency()
    }
)

watch(
    () => GAIT_TIME_KEYS.map((key) => localValue[key]),
    () => {
        enforceElapsedConsistency()
    }
)
</script>
